import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  FileText, Download, BookOpen, Loader2, ChevronDown, ChevronUp,
  Bookmark, ScrollText, Type, User, Copyright, Heart
} from 'lucide-react';

const API = '/api';

const ExportModal = ({ open, onOpenChange, project, user }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [config, setConfig] = useState({
    title: '',
    subtitle: '',
    author_name: '',
    copyright_year: new Date().getFullYear().toString(),
    copyright_holder: '',
    dedication: '',
    blurb: '',
    font_family: 'Georgia',
    font_size: 12,
    include_toc: true,
    include_title_page: true,
    include_copyright: true,
    page_size: 'A5',
    export_format: 'pdf',
  });

  useEffect(() => {
    if (open && project) {
      setConfig(prev => ({
        ...prev,
        title: project.title || '',
        author_name: user?.name || user?.username || '',
        copyright_holder: user?.name || user?.username || '',
      }));
      loadPreview();
    }
  }, [open, project]);

  const loadPreview = async () => {
    try {
      const res = await axios.get(`${API}/export/preview/${project.id}`);
      setPreview(res.data);
    } catch (e) {
      console.error('Preview load error:', e);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/export/generate`, {
        project_id: project.id,
        user_id: user?.id || '',
        ...config,
      }, { responseType: 'blob', timeout: 120000 });

      const ext = config.export_format === 'epub' ? 'epub' : 'pdf';
      const mime = config.export_format === 'epub' ? 'application/epub+zip' : 'application/pdf';
      
      // Check if the response is actually an error (JSON) disguised as a blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const err = JSON.parse(text);
        toast.error(err.detail || 'Export failed on the server.');
        return;
      }
      
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.title || 'manuscript'}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} exported successfully!`);
    } catch (e) {
      console.error('Export error:', e);
      const detail = e.response?.data?.detail || e.message || '';
      if (e.code === 'ECONNABORTED' || detail.includes('timeout')) {
        toast.error('Export timed out — your manuscript is very large. Try exporting as TXT first.');
      } else if (detail) {
        toast.error(`Export failed: ${detail}`);
      } else {
        toast.error('Export failed. Check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white" data-testid="export-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-emerald-400" />
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Export Manuscript
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Preview Summary */}
          {preview && (
            <Card className="bg-slate-800/60 border-slate-700/50">
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Manuscript Summary</span>
                  <span className="text-emerald-400 font-medium">{preview.total_words?.toLocaleString()} words</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-slate-400">Chapters</span>
                  <span className="text-cyan-400">{preview.chapter_count}</span>
                </div>
                {preview.chapters?.length > 0 && (
                  <div className="mt-2 space-y-0.5 max-h-24 overflow-y-auto">
                    {preview.chapters.map((ch, i) => (
                      <div key={i} className="flex justify-between text-[10px] text-slate-500">
                        <span className="truncate mr-2">{ch.title || `Chapter ${i + 1}`}</span>
                        <span className="whitespace-nowrap">{ch.word_count?.toLocaleString()} w</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Format Selection */}
          <div className="flex gap-2">
            {[
              { value: 'pdf', label: 'PDF', icon: FileText, desc: 'Print-ready' },
              { value: 'epub', label: 'ePub', icon: BookOpen, desc: 'E-readers' },
            ].map(fmt => (
              <button key={fmt.value}
                onClick={() => updateConfig('export_format', fmt.value)}
                data-testid={`format-${fmt.value}`}
                className={`flex-1 p-3 rounded-lg border transition-all ${
                  config.export_format === fmt.value
                    ? 'border-emerald-500 bg-emerald-500/10'
                    : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                }`}
              >
                <fmt.icon className={`w-5 h-5 mb-1 ${config.export_format === fmt.value ? 'text-emerald-400' : 'text-slate-500'}`} />
                <p className={`text-sm font-medium ${config.export_format === fmt.value ? 'text-emerald-300' : 'text-slate-400'}`}>{fmt.label}</p>
                <p className="text-[10px] text-slate-500">{fmt.desc}</p>
              </button>
            ))}
          </div>

          {/* Core Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 flex items-center gap-1"><Type className="w-3 h-3" /> Title</Label>
              <Input value={config.title} onChange={e => updateConfig('title', e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1"
                data-testid="export-title-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 flex items-center gap-1"><User className="w-3 h-3" /> Author</Label>
              <Input value={config.author_name} onChange={e => updateConfig('author_name', e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1"
                data-testid="export-author-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1"><Bookmark className="w-3 h-3" /> Subtitle (optional)</Label>
            <Input value={config.subtitle} onChange={e => updateConfig('subtitle', e.target.value)}
              placeholder="A Novel" className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1"
              data-testid="export-subtitle-input" />
          </div>

          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1"><ScrollText className="w-3 h-3" /> Back Cover Blurb (optional)</Label>
            <textarea value={config.blurb} onChange={e => updateConfig('blurb', e.target.value)}
              rows={3} placeholder="A gripping tale of..."
              className="w-full bg-slate-800 border border-slate-700 rounded-md text-white text-sm p-2 mt-1 resize-none focus:ring-1 focus:ring-emerald-500 focus:outline-none"
              data-testid="export-blurb-input" />
          </div>

          {/* Advanced Options Toggle */}
          <button onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
            data-testid="export-advanced-toggle">
            {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Advanced Options
          </button>

          {showAdvanced && (
            <Card className="bg-slate-800/40 border-slate-700/50">
              <CardContent className="p-3 space-y-3">
                <div>
                  <Label className="text-xs text-slate-400 flex items-center gap-1"><Heart className="w-3 h-3" /> Dedication (optional)</Label>
                  <Input value={config.dedication} onChange={e => updateConfig('dedication', e.target.value)}
                    placeholder="For my beloved..."
                    className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1"
                    data-testid="export-dedication-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400 flex items-center gap-1"><Copyright className="w-3 h-3" /> Copyright Year</Label>
                    <Input value={config.copyright_year} onChange={e => updateConfig('copyright_year', e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Copyright Holder</Label>
                    <Input value={config.copyright_holder} onChange={e => updateConfig('copyright_holder', e.target.value)}
                      className="bg-slate-800 border-slate-700 text-white h-8 text-sm mt-1" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Font Family</Label>
                    <select value={config.font_family} onChange={e => updateConfig('font_family', e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md text-white text-sm h-8 px-2 mt-1">
                      <option value="Georgia">Georgia (Classic)</option>
                      <option value="Garamond">Garamond (Elegant)</option>
                      <option value="Times New Roman">Times New Roman (Traditional)</option>
                      <option value="Palatino">Palatino (Modern Classic)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Page Size</Label>
                    <select value={config.page_size} onChange={e => updateConfig('page_size', e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md text-white text-sm h-8 px-2 mt-1">
                      <option value="A5">A5 (Trade Paperback)</option>
                      <option value="A4">A4 (Standard)</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <Switch checked={config.include_title_page} onCheckedChange={v => updateConfig('include_title_page', v)} />
                    Title Page
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <Switch checked={config.include_toc} onCheckedChange={v => updateConfig('include_toc', v)} />
                    Table of Contents
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <Switch checked={config.include_copyright} onCheckedChange={v => updateConfig('include_copyright', v)} />
                    Copyright Page
                  </label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Button */}
          <Button onClick={handleExport} disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white h-10"
            data-testid="export-generate-btn">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating {config.export_format.toUpperCase()}...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Export as {config.export_format.toUpperCase()}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
